/**
 * Компонент уведомления об обновлениях PWA
 * Показывает короткое полупрозрачное уведомление при обновлении приложения
 * Отслеживает как изменение версии, так и перерегистрацию Service Worker
 * 
 * ОПТИМИЗИРОВАНО ДЛЯ 24/7:
 * - Агрессивная проверка обновлений (30 секунд)
 * - Автоматическое применение без race conditions
 * - Fallback reload если что-то пошло не так
 * - Быстрая перезагрузка (500ms вместо 1500ms)
 */
import { useEffect, useState, memo, useRef } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import './UpdateNotification.css'

const APP_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.6'
const LAST_VERSION_KEY = 'pwa_last_version'
const LAST_SW_UPDATE_KEY = 'pwa_last_sw_update'

export const UpdateNotification = memo(function UpdateNotification() {
  const [showUpdateSuccess, setShowUpdateSuccess] = useState(false)
  const [updateMessage, setUpdateMessage] = useState({ title: '', message: '' })
  const [isUpdating, setIsUpdating] = useState(false)

  // Проверяем версию при загрузке приложения
  useEffect(() => {
    try {
      const lastVersion = localStorage.getItem(LAST_VERSION_KEY)
      console.log('[PWA] Version check:', { current: APP_VERSION, last: lastVersion })
      
      if (lastVersion && lastVersion !== APP_VERSION) {
        // Версия изменилась! Показываем уведомление
        console.log('[PWA] ✨ Version changed! Showing update notification:', lastVersion, '→', APP_VERSION)
        
        // Обновляем сохраненную версию
        localStorage.setItem(LAST_VERSION_KEY, APP_VERSION)
        
        // Устанавливаем сообщение о новой версии
        setUpdateMessage({
          title: 'Приложение обновлено!',
          message: `Установлена версия ${APP_VERSION}`
        })
        
        // Показываем уведомление
        setShowUpdateSuccess(true)
        
        // Автоматически скрываем через 4 секунды
        setTimeout(() => {
          console.log('[PWA] Hiding update notification')
          setShowUpdateSuccess(false)
        }, 4000)
      } else if (!lastVersion) {
        // Первый запуск - сохраняем текущую версию
        console.log('[PWA] First launch, saving version:', APP_VERSION)
        localStorage.setItem(LAST_VERSION_KEY, APP_VERSION)
      } else {
        // Версия не изменилась
        console.log('[PWA] No version change, no notification')
      }
    } catch (error) {
      console.error('[PWA] Error checking version:', error)
      // Продолжаем работу даже при ошибке
    }
  }, [])

  const {
    needRefresh: [needRefresh, setNeedRefresh],
  } = useRegisterSW({
    onRegistered(registration: ServiceWorkerRegistration | undefined) {
      console.log('[PWA] ✅ Service Worker registered, version:', APP_VERSION)
      
      // Проверка обновлений каждые 30 секунд
      // Для 24/7 приложения достаточно 30 секунд (5 секунд слишком агрессивно, засоряет логи)
      if (registration) {
        const checkInterval = setInterval(() => {
          console.log('[PWA] 🔄 Checking for updates (polling)...')
          registration.update().catch(err => {
            console.error('[PWA] ❌ Update check failed:', err)
          })
        }, 30000) // 30 секунд
        
        // Очистка при размонтировании
        return () => clearInterval(checkInterval)
      }
    },
    onRegisterError(error: Error) {
      console.error('[PWA] ❌ Service Worker registration failed:', error)
    },
  })

  // Флаг защиты от повторной обработки
  const isUpdatingRef = useRef(false)

  useEffect(() => {
    if (!needRefresh || isUpdatingRef.current) return

    isUpdatingRef.current = true
    setIsUpdating(true)
    setNeedRefresh(false)

    console.log('[PWA] 🎉 New SW waiting, sending SKIP_WAITING...')

    // Сохраняем timestamp обновления для показа уведомления после reload
    localStorage.setItem(LAST_SW_UPDATE_KEY, Date.now().toString())

    // Отправляем SKIP_WAITING в waiting SW (дополнительная подстраховка к autoUpdate).
    // Reload НЕ делаем сами — vite-plugin-pwa's autoUpdate уже имеет встроенный
    // controllerchange → reload. Два механизма reload = бесконечный цикл.
    navigator.serviceWorker.getRegistration().then(reg => {
      if (reg?.waiting) {
        reg.waiting.postMessage({ type: 'SKIP_WAITING' })
        console.log('[PWA] ✅ SKIP_WAITING sent to waiting SW')
      }
    }).catch(err => {
      console.error('[PWA] getRegistration failed:', err)
    })
  }, [needRefresh])

  // Проверяем, было ли обновление компонентов (SW перерегистрирован, но версия та же)
  useEffect(() => {
    const checkComponentUpdate = () => {
      try {
        const lastSWUpdate = localStorage.getItem(LAST_SW_UPDATE_KEY)
        const lastVersion = localStorage.getItem(LAST_VERSION_KEY)
        
        // Если есть timestamp обновления SW, но версия не менялась
        if (lastSWUpdate && lastVersion === APP_VERSION) {
          const updateTime = parseInt(lastSWUpdate, 10)
          const now = Date.now()
          const timeSinceUpdate = now - updateTime
          
          // Показываем уведомление только если обновление было недавно (в течение 5 секунд)
          // Это означает что мы только что перезагрузились после обновления SW
          if (timeSinceUpdate < 5000) {
            console.log('[PWA] ✨ Component update detected! Showing notification')
            
            setUpdateMessage({
              title: 'Обновление установлено!',
              message: 'Компоненты приложения обновлены'
            })
            
            setShowUpdateSuccess(true)
            
            // Очищаем флаг, чтобы не показывать повторно
            localStorage.removeItem(LAST_SW_UPDATE_KEY)
            
            // Автоматически скрываем через 4 секунды
            setTimeout(() => {
              console.log('[PWA] Hiding component update notification')
              setShowUpdateSuccess(false)
            }, 4000)
          } else {
            // Если прошло много времени - просто очищаем флаг
            localStorage.removeItem(LAST_SW_UPDATE_KEY)
          }
        }
      } catch (error) {
        console.error('[PWA] Error checking component update:', error)
        // Продолжаем работу даже при ошибке
      }
    }
    
    // Проверяем через 500мс после загрузки (после проверки версии)
    const timer = setTimeout(checkComponentUpdate, 500)
    return () => clearTimeout(timer)
  }, [])

  // Показываем уведомление об успешном обновлении
  if (showUpdateSuccess) {
    console.log('[PWA] Rendering success notification:', updateMessage)
    return (
      <div className="update-notification update-notification--success">
        <div className="update-notification__content">
          <span className="update-notification__icon">✨</span>
          <div className="update-notification__text">
            <div className="update-notification__title">{updateMessage.title}</div>
            <div className="update-notification__message">{updateMessage.message}</div>
          </div>
        </div>
      </div>
    )
  }

  // Показываем индикатор обновления (ПЕРЕД перезагрузкой)
  if (isUpdating) {
    console.log('[PWA] Rendering updating notification')
    return (
      <div className="update-notification">
        <div className="update-notification__content">
          <div className="update-notification__spinner"></div>
          <div className="update-notification__text">
            <div className="update-notification__title">Обновление...</div>
            <div className="update-notification__message">Подождите...</div>
          </div>
        </div>
      </div>
    )
  }

  return null
})
